import { useTheme } from '@mui/material'
import moment from 'moment'
import React, { useCallback } from 'react'
import { EventProps, momentLocalizer } from 'react-big-calendar'
// @ts-ignore
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useTranslation } from 'react-i18next'
import { TaskCalendarEvent } from 'src/components/locality/VesselTasksCalendarDialog'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import i18n from '../../../../i18n'
import { EventDetails, StyledCalendar } from './TaskCalendar.style'

const localizer = momentLocalizer(moment)

interface TaskCalendarProps {
  onClick: (event: TaskCalendarEvent, eventElement?: any) => void
  events: TaskCalendarEvent[]
  onMonthChange: (date: Date) => void
  paramsStartDate?: Date | string
  paramsEndDate?: Date | string
  selectedTasks?: TaskCalendarEvent[]
  defaultDate?: Date
}

const TaskCalendar = ({
  onClick,
  events,
  onMonthChange,
  paramsStartDate,
  paramsEndDate,
  selectedTasks,
  defaultDate,
}: TaskCalendarProps) => {
  const { t } = useTranslation(['common', 'task'])
  const theme = useTheme()

  // Store the last mouse event to access it in handleEventClick
  const lastMouseEventRef = React.useRef<React.MouseEvent | MouseEvent | null>(null)

  const calendarTranslations = {
    next: t('next', { ns: 'common' }),
    previous: t('back', { ns: 'common' }),
    today: t('today', { ns: 'task' }),
    month: t('month', { ns: 'common' }),
    day: t('day', { ns: 'common' }),
  }

  const dayPropGetter = date => {
    if (paramsStartDate && paramsEndDate && date >= new Date(paramsStartDate) && date <= new Date(paramsEndDate)) {
      return {
        style: {
          backgroundColor: getThemeColor(theme, MUIThemeColors.primaryLight), // Or any color you prefer
        },
      }
    }
    return {} // Return an empty object for dates outside the range
  }

  const eventPropGetter = (event: TaskCalendarEvent) => {
    const isSelected =
      selectedTasks?.some(
        selectedTask =>
          selectedTask.task.id === event.task.id &&
          selectedTask.startDate === event.startDate &&
          selectedTask.endDate === event.endDate,
      ) || false
    if (isSelected) {
      return {
        className: 'rbc-event-selected',
        style: {
          backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
          color: getThemeColor(theme, MUIThemeColors.white),
          borderColor: getThemeColor(theme, MUIThemeColors.primaryDark),
          fontWeight: 600,
        },
      }
    }

    return {
      style: {
        backgroundColor: getThemeColor(theme, MUIThemeColors.infoLight),
        color: getThemeColor(theme, MUIThemeColors.black),
        borderColor: getThemeColor(theme, MUIThemeColors.infoLight),
        fontWeight: 600,
      },
    }
  }

  const handleEventClick = useCallback(
    (event: TaskCalendarEvent) => {
      const overlay = document.querySelector('.rbc-overlay') as HTMLElement
      if (overlay) {
        overlay.style.display = 'none'
      }

      // Pass the stored mouse event to the onClick handler
      if (onClick) {
        onClick(event, lastMouseEventRef.current as any)
        // Clear the ref after use
        lastMouseEventRef.current = null
      }
    },
    [onClick],
  )

  return (
    <StyledCalendar
      culture={i18n.language}
      defaultDate={defaultDate}
      startAccessor={event => {
        return new Date(event.startDate)
      }}
      endAccessor={event => {
        const endDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate)
        endDate.setDate(endDate.getDate() + 1)
        return endDate
      }}
      localizer={localizer}
      events={events}
      onSelectEvent={handleEventClick}
      onNavigate={date => onMonthChange(date)}
      views={{ week: false, month: true, day: true }}
      popup
      popupOffset={{ x: 2, y: 10 }}
      dayPropGetter={dayPropGetter}
      eventPropGetter={eventPropGetter}
      components={{
        event: (props: EventProps<TaskCalendarEvent>) => (
          <CalendarEventContent {...props} onMouseEventCapture={lastMouseEventRef} />
        ),
      }}
      messages={calendarTranslations}
    />
  )
}

interface CalendarEventContentProps extends EventProps<TaskCalendarEvent> {
  onMouseEventCapture?: React.MutableRefObject<React.MouseEvent | MouseEvent | null>
}

const CalendarEventContent = (props: CalendarEventContentProps) => {
  const event = (props as EventProps<TaskCalendarEvent>).event
  const { onMouseEventCapture } = props
  const localityName = event?.locality?.name || event?.localityName || event?.locality
  const vesselName = event?.vesselName

  const handleMouseDown = (e: React.MouseEvent) => {
    // Capture the mouse event before react-big-calendar's onSelectEvent fires
    // Store both synthetic and native event to preserve all properties
    if (onMouseEventCapture) {
      // Store the synthetic event which has ctrlKey, metaKey, etc.
      onMouseEventCapture.current = e
    }
  }

  return (
    <EventDetails onMouseDown={handleMouseDown}>
      <span className="event-title" title={event?.title}>
        {event?.title}
      </span>
      {vesselName && (
        <>
          <span className="event-separator">•</span>
          <span className="event-locality" title={vesselName}>
            {vesselName}
          </span>
        </>
      )}
      {localityName && (
        <>
          <span className="event-separator">•</span>
          <span className="event-locality" title={localityName}>
            {localityName}
          </span>
        </>
      )}
    </EventDetails>
  )
}

export default TaskCalendar
